import React, { useState } from 'react'
import { DateTime } from 'luxon'
import WorkersByGroupChart from 'components/charts/WorkersByGroupChart'
import WorkersInBlockChart from 'components/charts/WorkersInBlockChart'
import TableComponent from 'components/common/TableComponent'
import ModalComponent from 'components/common/ModalComponent'
import { useWorkdaySummary } from 'hooks/useWorkdaySummary'
import { parseDate } from 'utils'

const FarmActivitySummaryTable = ({ farmActivityData }) => {
  const tableData = {
    columns: [
      { key: 'blockName', label: 'Block Name' },
      { key: 'groupName', label: 'Group Name' },
      { key: 'cropName', label: 'Crop' },
      { key: 'varietyName', label: 'Variety' },
      { key: 'numberOfPlants', label: 'Total' },
      { key: 'workerCount', label: 'Workers Number' },
      { key: 'firstScan', label: 'First Scan', cell: (value) => parseDate(value) },
      { key: 'lastScan', label: 'Last Scan', cell: (value) => parseDate(value) }
    ],
    rows: farmActivityData
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const LastActivitiesBoardTable = ({ lastActivitiesData }) => {
  const tableData = {
    columns: [
      { key: 'workerId', label: 'Worker ID' },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'lastActivity', label: 'Last Activity' },
      { key: 'timePassed', label: 'Time Passed' },
      { key: 'blockName', label: 'Block Name' },
      { key: 'location', label: 'Location', cell: (value) => value ? value.split(',').slice(0, 2).join(', ') : '' }
    ],
    rows: lastActivitiesData
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const getGroupWorkers = (group, lastActivitiesData = []) => {
  if (!group) return []

  const groupWorkers = [
    group.workers,
    group.workersList,
    group.workerList,
    group.workerDetails,
    group.workersDetails,
    group.workerData,
    group.workersData,
    group.details,
    group.items,
    group.data
  ].find(value => Array.isArray(value) || Array.isArray(value?.workers) || Array.isArray(value?.items))

  let workers = lastActivitiesData.filter(worker => worker.groupName === group.groupName)
  if (Array.isArray(groupWorkers)) {
    workers = groupWorkers
  } else if (Array.isArray(groupWorkers?.workers)) {
    workers = groupWorkers.workers
  } else if (Array.isArray(groupWorkers?.items)) {
    workers = groupWorkers.items
  }

  return workers.map(worker => ({
    workerId: worker.workerId || worker.worker_id || worker.oldId || worker.old_id || '',
    workerName: worker.workerName || worker.worker_name || worker.name || '',
    groupName: worker.groupName || worker.group_name || group.groupName || group.group_name || '',
    activityName: worker.activityName || worker.activity_name || worker.lastActivity || worker.last_activity || '',
    lastWorkingDay: worker.lastWorkingDay || worker.last_working_day || worker.date || worker.createdAt || worker.created_at || '',
    startTime: worker.startTime || worker.start_time || ''
  }))
}

const getBlockWorkers = (block, lastActivitiesData = []) => {
  if (!block) return []

  const blockWorkers = [
    block.workers,
    block.workersList,
    block.workerList,
    block.workerDetails,
    block.workersDetails,
    block.workerData,
    block.workersData,
    block.details,
    block.items,
    block.data
  ].find(value => Array.isArray(value) || Array.isArray(value?.workers) || Array.isArray(value?.items))

  let workers = lastActivitiesData.filter(worker => worker.blockName === block.blockName)
  if (Array.isArray(blockWorkers)) {
    workers = blockWorkers
  } else if (Array.isArray(blockWorkers?.workers)) {
    workers = blockWorkers.workers
  } else if (Array.isArray(blockWorkers?.items)) {
    workers = blockWorkers.items
  }

  return workers.map(worker => ({
    workerId: worker.workerId || worker.worker_id || worker.oldId || worker.old_id || '',
    workerName: worker.workerName || worker.worker_name || worker.name || '',
    blockName: worker.blockName || worker.block_name || block.blockName || block.block_name || '',
    activityName: worker.activityName || worker.activity_name || worker.lastActivity || worker.last_activity || '',
    deviceGroupName: worker.deviceGroupName || worker.device_group_name || worker.groupName || worker.group_name || '',
    eventTime: worker.eventTime || worker.event_time || worker.createdAt || worker.created_at || worker.time || ''
  }))
}

const WorkersByGroupDetailsTable = ({ group, lastActivitiesData }) => {
  const tableData = {
    columns: [
      { key: 'workerId', label: 'Worker ID' },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'groupName', label: 'Group Name' },
      { key: 'activityName', label: 'Activity Name' },
      { key: 'lastWorkingDay', label: 'Last Working Day' },
      { key: 'startTime', label: 'Start Time' }
    ],
    rows: getGroupWorkers(group, lastActivitiesData)
  }

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const WorkersInBlockDetailsTable = ({ block, lastActivitiesData }) => {
  const tableData = {
    columns: [
      { key: 'workerId', label: 'Worker ID' },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'blockName', label: 'Block Name' },
      { key: 'activityName', label: 'Activity Name' },
      { key: 'deviceGroupName', label: 'Device Group Name' },
      { key: 'eventTime', label: 'Event Time' }
    ],
    rows: getBlockWorkers(block, lastActivitiesData)
  }

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const LastDayWorkingSummary = () => {
  const { data, updateFilterDate } = useWorkdaySummary()
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedBlock, setSelectedBlock] = useState(null)

  const formatDate = (dateString) => {
    return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy')
  }

  const handleDateChange = (e) => {
    updateFilterDate(e.target.value)
  }

  const closeGroupDetailsModal = () => {
    setSelectedGroup(null)
  }

  const closeBlockDetailsModal = () => {
    setSelectedBlock(null)
  }

  return (
    <div className='p-6 bg-white-50 min-h-screen'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-600'>Last Working Day Summary</h1>
        </div>
        <div className='flex items-center gap-2'>
          <label htmlFor='datePicker' className='text-gray-700'>Select Date:</label>
          <input
            id='datePicker'
            type='date'
            value={data.filterDate}
            onChange={handleDateChange}
            className='border rounded px-2 py-1'
            max={data.currentDate}
          />
        </div>
      </div>
      <hr className='my-8 border-gray-300' />
      <div className='flex gap-6'>
        <section className='w-1/2'>
          <h1 className='text-2xl text-gray-500 mb-4'>Workers By Group</h1>
          <p className='text-gray-500'>Last Working Day ({formatDate(data.currentDate)})</p>
          {data.loading
            ? (
              <div className='w-full h-96 min-h-[600px] p-6 flex items-center justify-center'>
                <div className='text-gray-500'>Loading chart data...</div>
              </div>
              )
            : (
              <WorkersByGroupChart
                data={data.workersByGroup}
                totalWorkers={data.totalWorkers}
                onGroupClick={setSelectedGroup}
              />
              )}
        </section>
        <section className='w-1/2'>
          <h1 className='text-2xl text-gray-500 mb-4'>Workers In Block</h1>
          <p className='text-gray-500'>Last Working Day ({formatDate(data.currentDate)})</p>
          {data.loading
            ? (
              <div className='w-full h-96 min-h-[600px] p-6 flex items-center justify-center'>
                <div className='text-gray-500'>Loading chart data...</div>
              </div>
              )
            : (
              <WorkersInBlockChart data={data.workersInBlock} onBlockClick={setSelectedBlock} />
              )}
        </section>
      </div>
      <section className='mt-12'>
        <h1 className='text-2xl text-gray-500 mb-4'>Farm Activity Summary</h1>
        {data.loading
          ? (
            <div className='text-gray-500'>Loading farm activity data...</div>
            )
          : data.error
            ? (
              <div className='text-red-500'>{data.error}</div>
              )
            : (
              <FarmActivitySummaryTable farmActivityData={data.farmActivityData} />
              )}
      </section>
      <section className='mt-12'>
        <h1 className='text-2xl text-gray-500 mb-4'>Last Activities Board</h1>
        {data.loading
          ? (
            <div className='text-gray-500'>Loading last activities data...</div>
            )
          : data.error
            ? (
              <div className='text-red-500'>{data.error}</div>
              )
            : (
              <LastActivitiesBoardTable lastActivitiesData={data.lastActivitiesData} />
              )}
      </section>
      <ModalComponent
        isOpen={Boolean(selectedGroup)}
        onClose={closeGroupDetailsModal}
        title={`Workers in ${selectedGroup?.groupName || 'Group'}`}
        maxWidth='5xl'
        customFooter={(
          <button
            type='button'
            onClick={closeGroupDetailsModal}
            className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
          >
            Close
          </button>
        )}
      >
        <WorkersByGroupDetailsTable group={selectedGroup} lastActivitiesData={data.lastActivitiesData} />
      </ModalComponent>
      <ModalComponent
        isOpen={Boolean(selectedBlock)}
        onClose={closeBlockDetailsModal}
        title={`Workers in ${selectedBlock?.blockName || 'Block'}`}
        maxWidth='6xl'
        customFooter={(
          <button
            type='button'
            onClick={closeBlockDetailsModal}
            className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
          >
            Close
          </button>
        )}
      >
        <WorkersInBlockDetailsTable block={selectedBlock} lastActivitiesData={data.lastActivitiesData} />
      </ModalComponent>
    </div>
  )
}

export default LastDayWorkingSummary
